/**
 * Machine finder - markup and data in sections/machine-finder.liquid, styles in machine-finder.css.
 *
 * Brand -> Model (type-to-search) -> the brand's accessory collection filtered to that model
 * (?filter.p.m.custom.<fitment metafield>=<model>). The chosen machine is remembered in the
 * browser ('franklinsMachine'), which the product pages use for "Fits your <machine>".
 *
 * Accessory search: once a machine's accessories are showing, a search box only shows what fits
 * that machine. Suggestions come from the machine-finder-results section (Section Rendering API),
 * which runs the normal shop search with the same filters as the full results page.
 */
(function () {
  'use strict';

  var TYPES = ['sewing', 'embroidery', 'overlocker', 'coverstitch'];
  var LABELS = { overlocker: 'overlocker', coverstitch: 'coverstitch' };
  var MAX_MODELS = 60;
  // Range names that customers often leave out ("1300" for "Innov-is 1300", "MC" for "Memory Craft").
  var RANGE_RE = /^(innov-?is|stellaire|luminaire|aveneer|celeste|horizon)\s*/;

  function clean(list) { return (list || []).map(function (m) { return String(m).trim(); }).filter(Boolean); }
  function escapeRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function readJson(root, attr, fallback) {
    var el = root.querySelector('[' + attr + ']');
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  function initFinder(root) {
    var data = readJson(root, 'data-mf-data', null);
    if (!data || !data.length) return;

    // One model list per brand, sewing machines first. Each model remembers which list it came from.
    data.forEach(function (b) {
      var seen = {};
      b.all = [];
      TYPES.forEach(function (t) {
        clean(b.models && b.models[t]).forEach(function (m) {
          if (seen[m]) return;
          seen[m] = true;
          b.all.push({ model: m, type: t });
        });
      });
    });

    // Brand names (and vendor spellings) customers might type in front of the model.
    var brandWords = [];
    data.forEach(function (b) {
      [b.brand, b.vendor].forEach(function (n) {
        n = String(n || '').toLowerCase().trim();
        if (n && brandWords.indexOf(n) < 0) brandWords.push(n);
      });
    });
    brandWords.sort(function (a, b) { return b.length - a.length; });
    var BRAND_RE = new RegExp('^(' + brandWords.map(function (n) { return escapeRe(n).replace(/\s+/g, '\\s*'); }).join('|') + ')\\s*');

    // "dx-7", "DX 7" and "dx7" all match "DX7".
    // Brand and range names are optional, so "1300", "NV1300" and "Brother Innov-is 1300" all match "Innov-is 1300".
    function norm(s) {
      return String(s || '').toLowerCase().trim()
        .replace(/\s*\((overlocker|coverstitch)\)\s*$/, '')
        .replace(BRAND_RE, '')
        .replace(RANGE_RE, '')
        .replace(/^memory\s*craft\s*/, 'mc')
        .replace(/[^a-z0-9]/g, '')
        .replace(/^nv(?=\d)/, '');
    }

    var brandSel = root.querySelector('[data-mf-brand]');
    var modelInput = root.querySelector('[data-mf-model]');
    var modelBox = root.querySelector('[data-mf-models]');
    var goBtn = root.querySelector('[data-mf-go]');
    var msg = root.querySelector('[data-mf-message]');
    var form = root.querySelector('.machine-finder__form');
    var mActive = -1;

    var params = new URLSearchParams(window.location.search);
    // A machine's accessories are showing: reveal the accessory search (see machine-finder.css).
    if (data.some(function (b) { return params.get('filter.p.m.custom.' + b.key); })) root.classList.add('machine-finder--filtered');

    function showMessage(text) { msg.textContent = text || ''; msg.hidden = !text; }
    function currentBrand() { return data[brandSel.value]; }

    // Placeholder example: the first model with a number in it, without the range name.
    function example(brand) {
      var names = brand.all.map(function (x) { return x.model.replace(/^(Innov-is|Stellaire|Luminaire|Aveneer)\s+/, ''); });
      return names.find(function (n) { return n.length > 2 && /\d/.test(n); }) || names[0] || '';
    }

    // ----- Model list -----

    // Models for the box as typed: those that start with it first, then those that contain it. Empty box = all.
    function modelMatches(typed) {
      var brand = currentBrand();
      if (!brand) return [];
      var t = norm(typed);
      if (!t) return brand.all.slice();
      var starts = [], contains = [];
      brand.all.forEach(function (x) {
        var i = norm(x.model).indexOf(t);
        if (i === 0) starts.push(x); else if (i > 0) contains.push(x);
      });
      return starts.concat(contains);
    }

    function closeModels() {
      modelBox.hidden = true;
      modelBox.innerHTML = '';
      mActive = -1;
      modelInput.setAttribute('aria-expanded', 'false');
      modelInput.removeAttribute('aria-activedescendant');
    }

    function pickModel(x) {
      modelInput.value = x.model;
      closeModels();
      goBtn.disabled = false;
      showMessage('');
      // Let the accessory search know the machine has changed.
      modelInput.dispatchEvent(new Event('change'));
    }

    function openModels() {
      var brand = currentBrand();
      if (!brand || modelInput.disabled) { closeModels(); return; }
      var typed = modelInput.value.trim();
      var matches = modelMatches(typed);
      modelBox.innerHTML = '';
      mActive = -1;
      modelInput.removeAttribute('aria-activedescendant');
      matches.slice(0, MAX_MODELS).forEach(function (x, i) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'machine-finder__option';
        opt.id = modelBox.id + '-' + i;
        opt.tabIndex = -1;
        opt.setAttribute('role', 'option');
        opt.setAttribute('aria-selected', 'false');
        var name = document.createElement('span');
        name.textContent = x.model;
        opt.appendChild(name);
        if (LABELS[x.type]) {
          var kind = document.createElement('span');
          kind.className = 'machine-finder__option-type';
          kind.textContent = LABELS[x.type];
          opt.appendChild(kind);
        }
        // Keep the cursor in the box while choosing (desktop), then pick on tap or click.
        opt.addEventListener('mousedown', function (e) { e.preventDefault(); });
        opt.addEventListener('click', function () { pickModel(x); });
        modelBox.appendChild(opt);
      });
      if (!matches.length) {
        var none = document.createElement('p');
        none.className = 'machine-finder__results-empty';
        none.textContent = 'No ' + brand.brand + ' model matches “' + typed + '”. Check the label on your machine, or ask in any of our stores.';
        modelBox.appendChild(none);
      } else if (matches.length > MAX_MODELS) {
        var more = document.createElement('p');
        more.className = 'machine-finder__results-empty';
        more.textContent = 'Keep typing to narrow down ' + matches.length + ' models…';
        modelBox.appendChild(more);
      }
      modelBox.hidden = false;
      modelBox.scrollTop = 0;
      modelInput.setAttribute('aria-expanded', 'true');
    }

    function refreshModels(keepValue) {
      var brand = currentBrand();
      closeModels();
      if (!keepValue) modelInput.value = '';
      showMessage('');
      if (!brand) { modelInput.disabled = true; goBtn.disabled = true; return; }
      var eg = example(brand);
      modelInput.placeholder = eg ? 'Type or choose your model, e.g. ' + eg : 'Type or choose your model';
      modelInput.disabled = false;
      goBtn.disabled = !modelInput.value.trim();
    }

    // An exact match first (ignoring spaces, dashes and brand/range names), then a single partial match.
    function findModel(typed) {
      var brand = currentBrand();
      if (!brand || !typed) return null;
      var t = norm(typed);
      // Nothing left after removing the brand/range name (e.g. just "Janome"): not a model.
      if (!t) return null;
      var exact = brand.all.find(function (x) { return norm(x.model) === t; });
      if (exact) return exact;
      var partial = brand.all.filter(function (x) { return norm(x.model).indexOf(t) > -1; });
      return partial.length === 1 ? partial[0] : null;
    }

    brandSel.addEventListener('change', function () { refreshModels(false); });
    modelInput.addEventListener('input', function () { goBtn.disabled = !modelInput.value.trim(); showMessage(''); openModels(); });
    modelInput.addEventListener('focus', openModels);
    modelInput.addEventListener('click', function () { if (modelBox.hidden) openModels(); });
    modelInput.addEventListener('keydown', function (e) {
      var opts = modelBox.hidden ? [] : Array.prototype.slice.call(modelBox.querySelectorAll('[role="option"]'));
      if (e.key === 'ArrowDown' && modelBox.hidden) {
        e.preventDefault();
        openModels();
      } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && opts.length) {
        e.preventDefault();
        mActive = e.key === 'ArrowDown' ? (mActive + 1) % opts.length : (mActive <= 0 ? opts.length - 1 : mActive - 1);
        opts.forEach(function (o, i) { o.setAttribute('aria-selected', i === mActive ? 'true' : 'false'); });
        modelInput.setAttribute('aria-activedescendant', opts[mActive].id);
        opts[mActive].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && opts[mActive]) {
        e.preventDefault();
        opts[mActive].click();
      } else if ((e.key === 'Escape' && !modelBox.hidden) || e.key === 'Tab') {
        closeModels();
      }
    });
    // Close the list when tapping or clicking anywhere else (pointerdown also fires on iPhone).
    document.addEventListener('pointerdown', function (e) {
      if (!modelBox.hidden && e.target !== modelInput && !modelBox.contains(e.target)) closeModels();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      closeModels();
      var brand = currentBrand();
      if (!brand) { showMessage('Please choose your machine brand first.'); return; }
      var hit = findModel(modelInput.value);
      if (!hit) {
        showMessage("We couldn't find “" + modelInput.value.trim() + "”. Pick a model from the list, or ask in any of our stores and we'll find the right part for you.");
        return;
      }
      modelInput.value = hit.model;
      modelInput.blur();
      try { localStorage.setItem('franklinsMachine', JSON.stringify({ brand: brandSel.value, brandName: brand.brand, type: hit.type, model: hit.model })); } catch (err) {}
      var here = readJson(root, 'data-mf-here', '') || '';
      window.location.href = (here || brand.url) + '?filter.p.m.custom.' + encodeURIComponent(brand.key) + '=' + encodeURIComponent(hit.model);
    });

    // Pre-fill from the filter in the URL, or the customer's last choice.
    var preset = null;
    data.forEach(function (b, i) {
      var v = params.get('filter.p.m.custom.' + b.key);
      if (v && !preset) preset = { brand: String(i), model: v };
    });
    if (!preset) {
      try { preset = JSON.parse(localStorage.getItem('franklinsMachine') || 'null'); } catch (err) { preset = null; }
      if (preset && preset.brandName) {
        var bi = data.findIndex(function (b) { return b.brand.toLowerCase() === String(preset.brandName).toLowerCase(); });
        if (bi > -1) preset.brand = String(bi); else preset = null;
      }
    }
    if (preset && data[preset.brand]) {
      brandSel.value = preset.brand;
      refreshModels(false);
      modelInput.value = preset.model || '';
      goBtn.disabled = !modelInput.value;
    } else if (data.length === 1) {
      brandSel.value = '0';
      refreshModels(false);
    }

    initSearch(root, data, currentBrand, findModel, clean);
    initSavedMachine(root, data, params);
  }

  // ----- Accessory search -----
  // Searches the shop; once a machine is chosen in the finder (or remembered from last time) it only
  // shows what fits that machine. With a brand but no model, it searches that brand's accessories.
  function initSearch(root, data, currentBrand, findModel, clean) {
    var sForm = root.querySelector('[data-mf-search-form]');
    if (!sForm) return;
    var brandSel = root.querySelector('[data-mf-brand]');
    var modelInput = root.querySelector('[data-mf-model]');
    var sInput = sForm.querySelector('[data-mf-q]');
    var sBox = sForm.querySelector('[data-mf-results]');
    var sScope = sForm.querySelector('[data-mf-scope]');
    var sUrl = sForm.getAttribute('action');
    var sTypes = clean(readJson(root, 'data-mf-search-types', []));
    var sVendors = data.map(function (b) { return b.vendor || b.brand; });
    var sAll = false, sTimer = null, sReq = 0, sActive = -1, sLast = '';

    // The machine chosen in the finder right now (pre-filled from the page's filter or the customer's last choice).
    function machine() {
      var b = currentBrand();
      var hit = b && modelInput.value.trim() ? findModel(modelInput.value) : null;
      return hit ? { brand: b.brand, key: b.key, model: hit.model } : null;
    }

    function query(term) {
      var p = new URLSearchParams();
      p.set('q', term);
      p.set('type', 'product');
      p.set('options[prefix]', 'last');
      var m = sAll ? null : machine();
      if (m) {
        p.append('filter.p.m.custom.' + m.key, m.model);
      } else {
        // No model yet: accessories only, from the brand chosen above or else from all the finder's brands.
        var b = sAll ? null : currentBrand();
        sTypes.forEach(function (t) { p.append('filter.p.product_type', t); });
        (b ? [b.vendor || b.brand] : sVendors).forEach(function (v) { p.append('filter.p.vendor', v); });
      }
      return p.toString();
    }

    function textLink(text, onClick) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'machine-finder__link';
      b.textContent = text;
      b.addEventListener('click', onClick);
      return b;
    }

    function setScope(all) {
      sAll = all;
      showScope();
      sLast = '';
      sInput.focus();
      suggest();
    }

    function showScope() {
      var m = machine();
      sScope.textContent = '';
      if (m && !sAll) {
        var strong = document.createElement('strong');
        strong.textContent = m.brand + ' ' + m.model;
        sScope.append('Only showing what fits your ', strong, ' · ', textLink('Search all accessories', function () { setScope(true); }));
      } else if (m) {
        sScope.append('Searching all accessories · ', textLink('Only what fits my ' + m.brand + ' ' + m.model, function () { setScope(false); }));
      } else if (currentBrand()) {
        sScope.textContent = 'Searching ' + currentBrand().brand + ' accessories. Add your model above to only see what fits.';
      } else {
        sScope.textContent = 'Choose your machine above to only see what fits.';
      }
    }

    function close() {
      clearTimeout(sTimer);
      sBox.hidden = true;
      sBox.innerHTML = '';
      sActive = -1;
      sLast = '';
      sInput.setAttribute('aria-expanded', 'false');
      sInput.removeAttribute('aria-activedescendant');
    }

    function render(res, term, qs) {
      var m = sAll ? null : machine();
      var items = res.items || [];
      sBox.innerHTML = '';
      sActive = -1;
      sInput.removeAttribute('aria-activedescendant');
      items.forEach(function (item, i) {
        var a = document.createElement('a');
        a.className = 'machine-finder__result';
        a.href = item.url;
        a.id = sBox.id + '-' + i;
        a.tabIndex = -1;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        var img = document.createElement(item.image ? 'img' : 'span');
        img.className = 'machine-finder__result-img';
        if (item.image) { img.src = item.image; img.alt = ''; img.width = 48; img.height = 48; img.loading = 'lazy'; }
        var body = document.createElement('span');
        body.className = 'machine-finder__result-text';
        var title = document.createElement('span');
        title.className = 'machine-finder__result-title';
        title.textContent = item.title;
        var meta = document.createElement('span');
        meta.className = 'machine-finder__result-meta';
        meta.textContent = item.price + (item.available ? '' : ' · Sold out');
        body.appendChild(title);
        body.appendChild(meta);
        a.appendChild(img);
        a.appendChild(body);
        sBox.appendChild(a);
      });
      if (!items.length) {
        var none = document.createElement('p');
        none.className = 'machine-finder__results-empty';
        none.textContent = 'Nothing found for “' + term + '”' + (m ? ' that fits your ' + m.brand + ' ' + m.model + '. ' : '. ');
        if (m) none.appendChild(textLink('Search all accessories', function () { setScope(true); }));
        sBox.appendChild(none);
      } else if (res.count > items.length) {
        var more = document.createElement('a');
        more.className = 'machine-finder__result-all';
        more.href = sUrl + '?' + qs;
        more.textContent = 'See all ' + res.count + ' results';
        sBox.appendChild(more);
      }
      sBox.hidden = false;
      sInput.setAttribute('aria-expanded', 'true');
    }

    function suggest() {
      var term = sInput.value.trim();
      if (term.length < 2) { close(); return; }
      var qs = query(term);
      if (qs === sLast) return;
      sLast = qs;
      var id = ++sReq;
      fetch(sUrl + '?' + qs + '&section_id=machine-finder-results')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (id !== sReq) return; // a newer search has started
          var res = { count: 0, items: [] };
          var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-mf-results-json]');
          try { if (el) res = JSON.parse(el.textContent); } catch (err) {}
          render(res, term, qs);
        })
        .catch(function () { if (id === sReq) close(); });
    }

    sInput.addEventListener('input', function () { clearTimeout(sTimer); sTimer = setTimeout(suggest, 200); });
    sInput.addEventListener('focus', function () { if (sBox.hidden) suggest(); });
    sInput.addEventListener('keydown', function (e) {
      var opts = sBox.hidden ? [] : Array.prototype.slice.call(sBox.querySelectorAll('[role="option"]'));
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && opts.length) {
        e.preventDefault();
        sActive = e.key === 'ArrowDown' ? (sActive + 1) % opts.length : (sActive <= 0 ? opts.length - 1 : sActive - 1);
        opts.forEach(function (o, i) { o.setAttribute('aria-selected', i === sActive ? 'true' : 'false'); });
        sInput.setAttribute('aria-activedescendant', opts[sActive].id);
        opts[sActive].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && opts[sActive]) {
        e.preventDefault();
        window.location.href = opts[sActive].href;
      } else if (e.key === 'Escape' && !sBox.hidden) {
        e.preventDefault();
        close();
      }
    });
    // pointerdown rather than click: on iPhone, taps on plain page areas don't fire click.
    document.addEventListener('pointerdown', function (e) { if (!sForm.contains(e.target)) close(); });
    sForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var term = sInput.value.trim();
      if (!term) { sInput.focus(); return; }
      window.location.href = sUrl + '?' + query(term);
    });
    // Keep the search in step with the machine chosen above.
    brandSel.addEventListener('change', function () { sAll = false; showScope(); close(); });
    modelInput.addEventListener('input', showScope);
    modelInput.addEventListener('change', showScope);
    showScope();
  }

  // ----- Saved machine -----
  // Customer has already told us their machine: show one line instead of the whole form,
  // and point the brand/type links on this page straight at what fits.
  function initSavedMachine(root, data, params) {
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem('franklinsMachine') || 'null'); } catch (err) { saved = null; }
    var ctx = readJson(root, 'data-mf-ctx', { template: '', url: '', types: [] });
    var sb = saved && saved.brandName ? data.find(function (b) { return b.brand.toLowerCase() === String(saved.brandName).toLowerCase(); }) : null;
    if (!sb || !saved.model) return;
    var param = 'filter.p.m.custom.' + sb.key;
    var mine = function (u) { return u + (u.indexOf('?') > -1 ? '&' : '?') + param + '=' + encodeURIComponent(saved.model); };
    var typeUrls = (ctx.types || []).map(function (h) { return '/collections/' + h; });
    var onCollection = ctx.template === 'collection';
    var path = location.pathname.replace(/\/$/, '');
    var isType = typeUrls.indexOf(path) > -1 || !!readJson(root, 'data-mf-here', '');
    var isMyBrand = sb.url.replace(/\/$/, '') === path;
    if (onCollection && !isType && !isMyBrand) return; // another brand's collection: keep the normal finder
    var current = params.get(param);
    if (current && current !== saved.model) return; // looking at a different machine: keep the normal finder

    var bar = root.querySelector('[data-mf-saved]');
    var label = saved.brandName + ' ' + saved.model;
    var html;
    if (onCollection && current === saved.model) {
      var all = new URL(location.href); all.searchParams.delete(param);
      html = '<p class="machine-finder__saved-text">Showing accessories that fit your <strong></strong></p>' +
        '<div class="machine-finder__saved-links"><a class="machine-finder__link" href="' + all.pathname + all.search + '">Show everything</a>' +
        '<button type="button" class="machine-finder__link" data-mf-change>Change machine</button></div>';
    } else if (onCollection) {
      html = '<p class="machine-finder__saved-text">Your machine: <strong></strong></p>' +
        '<div class="machine-finder__saved-links"><a class="button button--primary" href="' + mine(location.pathname) + '">Only show what fits</a>' +
        '<button type="button" class="machine-finder__link" data-mf-change>Change machine</button></div>';
    } else {
      html = '<p class="machine-finder__saved-text">Your machine: <strong></strong></p>' +
        '<div class="machine-finder__saved-links"><a class="button button--primary" href="' + mine(sb.url) + '">See everything that fits</a>' +
        '<button type="button" class="machine-finder__link" data-mf-change>Change machine</button></div>';
      // Brand and type tiles elsewhere on this page go straight to what fits.
      document.querySelectorAll('a[href^="/collections/"]').forEach(function (a) {
        if (root.contains(a)) return;
        var p = a.getAttribute('href').split('?')[0].replace(/\/$/, '');
        if (typeUrls.indexOf(p) > -1 || p === sb.url.replace(/\/$/, '')) a.setAttribute('href', mine(p));
      });
    }
    bar.innerHTML = html;
    bar.querySelector('strong').textContent = label;
    var formEl = root.querySelector('.machine-finder__form');
    var textEl = root.querySelector('.machine-finder__text');
    var modelInput = root.querySelector('[data-mf-model]');
    formEl.hidden = true; if (textEl) textEl.hidden = true; bar.hidden = false;
    bar.querySelector('[data-mf-change]').addEventListener('click', function () {
      bar.hidden = true; formEl.hidden = false; if (textEl) textEl.hidden = false; modelInput.focus();
    });
  }

  function start() { document.querySelectorAll('[data-machine-finder]').forEach(initFinder); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
